// Cliente de Modo Auditor (2026-09-09) — ver packages/web/src/api/lib/hce-auditoria.ts.
// Dos credenciales posibles según quién llama: el admin (token de
// hceAdminSession.ts) o el inspector (token de auditorSession.ts) — cada
// función que lee/cierra recibe el bearer explícito, nunca lo resuelve sola,
// mismo criterio que descargarExportacionLegal(tokenAdmin?) en hceVault.ts.
import { vaultUrl } from './hceVault';

/** Exportado: lo reusa lib/hceComite.ts (Comité de Historia Clínica) — mismo criterio de bearer/descarga en ambos, sin duplicar. */
export function bearer(token: string): Record<string, string> {
  return { 'content-type': 'application/json', authorization: `Bearer ${token}` };
}

export type EstadoItemChecklist = 'bien' | 'atencion' | 'critico' | 'informativo';

/** "Ver evidencia" (2026-09-09) — nunca lista pacientes por nombre, ver hce-auditoria.ts. `href` opcional por fila (2026-09-10): la fila puede llevar a una pantalla real donde resolver el hallazgo. */
export type EvidenciaItem = { filas: { label: string; valor: string; href?: string }[] } | { texto: string };

export type ItemChecklistAuditoria = {
  clave: string;
  titulo: string;
  cita: string;
  estado: EstadoItemChecklist;
  detalle: string;
  evidencia?: EvidenciaItem;
};

export type ChecklistAuditoria = {
  generadoEn: string;
  clinicName: string;
  items: ItemChecklistAuditoria[];
};

/** BA-C-07: la URL lleva un código de un solo uso en el fragmento (`#e=`), no el token de sesión; el PIN alterno es de 8 dígitos. */
export type ResultadoGenerarAcceso = { reference: string; pin: string; url: string; exp: number };

export async function generarAccesoAuditoria(tokenAdmin: string, inspectorNombre?: string): Promise<ResultadoGenerarAcceso> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/generar'), {
    method: 'POST',
    headers: bearer(tokenAdmin),
    body: JSON.stringify({ inspectorNombre }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'No se pudo generar el acceso de auditoría.');
  return body as ResultadoGenerarAcceso;
}

export type ResultadoCanjearPin = { token: string; exp: number };

export async function canjearPinAuditoria(subdominio: string, pin: string): Promise<ResultadoCanjearPin> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/canjear-pin'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ subdominio, pin }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'PIN incorrecto o vencido.');
  return body as ResultadoCanjearPin;
}

/** BA-C-07: canjea el código de un solo uso del enlace del QR por la sesión del inspector (el token nunca viaja en una URL). */
export async function canjearEnlaceAuditoria(subdominio: string, codigo: string): Promise<ResultadoCanjearPin> {
  let res: Response;
  try {
    res = await fetch(vaultUrl('/api/hce/auditoria/canjear-enlace'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ subdominio, codigo }),
    });
  } catch {
    // Sin respuesta: no se sabe si el servidor llegó a consumir el código.
    throw new ErrorCanjeEnlace('No se pudo contactar al servidor. Vuelve a abrir el enlace; si dice que ya fue usado, pide un acceso nuevo al consultorio.', false);
  }
  const body = await res.json().catch(() => ({}));
  // 429: el servidor rechazó la petición SIN canjear, así que el código sigue vigente y el enlace original vuelve a servir.
  if (!res.ok) throw new ErrorCanjeEnlace(body?.error || 'Enlace inválido, vencido o ya usado.', res.status === 429);
  return body as ResultadoCanjearPin;
}

/** Error del canje del enlace; `noConsumido` es true solo cuando el servidor garantiza que el código NO se gastó (volver a abrir el enlace sirve). */
export class ErrorCanjeEnlace extends Error {
  constructor(
    message: string,
    readonly noConsumido: boolean,
  ) {
    super(message);
    this.name = 'ErrorCanjeEnlace';
  }
}

export async function obtenerChecklistAuditoria(token: string): Promise<ChecklistAuditoria> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/checklist'), { headers: bearer(token) });
  if (res.status === 401) throw new Error('Sesión vencida — pedile al consultorio un acceso nuevo.');
  if (!res.ok) throw new Error('No se pudo cargar el checklist.');
  return (await res.json()) as ChecklistAuditoria;
}

export type AuditoriaHistorialItem = {
  id: number;
  reference: string;
  estado: 'activa' | 'expirada' | 'cerrada';
  creadoPorNombre: string;
  inspectorNombre: string | null;
  createdAt: string;
  tieneConstancia: boolean;
};

export async function listarHistorialAuditorias(tokenAdmin: string): Promise<AuditoriaHistorialItem[]> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/historial'), { headers: bearer(tokenAdmin) });
  if (!res.ok) throw new Error('No se pudo cargar el historial de auditorías.');
  const body = await res.json();
  return body.historial as AuditoriaHistorialItem[];
}

/** Exportado: lo reusa lib/hceComite.ts — mismo blob-download. */
export async function descargarArchivo(res: Response, nombrePorDefecto: string): Promise<void> {
  if (!res.ok) throw new Error('No se pudo generar el archivo.');
  const disposition = res.headers.get('content-disposition') ?? '';
  const nombreSugerido = /filename="([^"]+)"/.exec(disposition)?.[1] ?? nombrePorDefecto;
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = nombreSugerido;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Cierra la visita (revoca el token de auditor de inmediato) y descarga la constancia — la puede disparar el admin o el propio inspector. */
export async function cerrarVisitaAuditoria(token: string, sesionId: number): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/${sesionId}/constancia`), { method: 'POST', headers: bearer(token) });
  await descargarArchivo(res, `constancia-auditoria-${sesionId}.pdf`);
}

export async function descargarConstanciaAuditoria(tokenAdmin: string, sesionId: number): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/${sesionId}/constancia`), { headers: bearer(tokenAdmin) });
  await descargarArchivo(res, `constancia-auditoria-${sesionId}.pdf`);
}

export async function descargarResumenCumplimiento(tokenAdmin: string): Promise<void> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/resumen-pdf'), { headers: bearer(tokenAdmin) });
  await descargarArchivo(res, 'resumen-cumplimiento.pdf');
}

/** Modo Auditor 2026-09-10 — admin O auditor, no es dato de paciente (mismo criterio que el resto del ítem "destruccion-certificada"). */
export async function descargarCertificadoDestruccion(token: string, reference: string): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/certificado-destruccion-pdf?reference=${encodeURIComponent(reference)}`), { headers: bearer(token) });
  await descargarArchivo(res, `certificado-destruccion-${reference}.pdf`);
}

/** Ficha de lote (ítem "inventario-trazabilidad-sanitaria") — mismo patrón que descargarCertificadoDestruccion. */
export async function descargarFichaLote(token: string, lotId: string): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/lote-ficha-pdf?lotId=${encodeURIComponent(lotId)}`), { headers: bearer(token) });
  await descargarArchivo(res, `ficha-lote-${lotId}.pdf`);
}

/** Certificado de disposición final de una entrega al gestor (ítem "entregas-gestor-desechos-periodo", Fase 3 ítem 24b) — mismo patrón que descargarFichaLote. */
export async function descargarEntregaCertificado(token: string, entregaId: string): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/entrega-certificado-pdf?entregaId=${encodeURIComponent(entregaId)}`), { headers: bearer(token) });
  await descargarArchivo(res, `certificado-entrega-${entregaId}.pdf`);
}

/** Modo Auditor 2026-09-10 — la evidencia del checklist solo mostraba 10 de 2757 accesos reales; CSV completo, admin o auditor (no es dato de paciente). */
export async function descargarAccesosCsv(token: string): Promise<void> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/accesos-csv'), { method: 'POST', headers: bearer(token), body: '{}' });
  await descargarArchivo(res, 'accesos.csv');
}

/** Ronda 2, 2026-09-10 — la evidencia de "firma-electronica" solo mostraba un conteo por tipo; CSV completo, admin o auditor (no es dato de paciente). */
export async function descargarFirmasCsv(token: string): Promise<void> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/firmas-csv'), { headers: bearer(token) });
  await descargarArchivo(res, 'firmas.csv');
}

export type VerificacionPaciente = {
  nombre: string;
  idNumber: string;
  archivoEstado: 'activo' | 'pasivo' | 'sin-clasificar';
  ultimaAtencion: string | null;
  tieneConsentimiento: boolean;
};

/**
 * "Verificar un paciente puntual" (Modo Auditor sección C, 2026-09-10) —
 * único punto donde el token de auditor externo llega a identidad de
 * paciente, y solo por decisión propia (cédula exacta). Admin o auditor.
 */
export async function verificarPacienteAuditoria(token: string, cedula: string): Promise<VerificacionPaciente> {
  // H-001: la cédula va en el cuerpo (POST), nunca en la URL.
  const res = await fetch(vaultUrl('/api/hce/auditoria/verificar-paciente'), {
    method: 'POST',
    headers: bearer(token),
    body: JSON.stringify({ cedula }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'No se pudo verificar el paciente.');
  return body as VerificacionPaciente;
}

// --- MT-051: visor del registro de accesos (filtros en el servidor, paginación y exportación con hash) ---------------------------------------------

export type FiltrosAccesos = {
  usuario?: string;
  /** Solo el admin; el Modo Auditor no puede filtrar por paciente (el servidor lo rechaza). */
  paciente?: string;
  accion?: string;
  resultado?: 'ok' | 'denegado' | 'error';
  /** `AAAA-MM-DD`, hora de Ecuador, inclusive. */
  desde?: string;
  hasta?: string;
};

export type FilaAcceso = {
  seq: number | null;
  cuando: string;
  usuario: string | null;
  profesionalId: string | null;
  accion: string;
  resultado: 'ok' | 'denegado' | 'error';
  recurso: string | null;
  /** `Patient/<id>` para el admin; un seudónimo `P-` + 20 hex en el Modo Auditor. */
  paciente: string | null;
  metodo: string | null;
  modulo: string | null;
  http: number | null;
  ip: string | null;
  hash: string | null;
};

export type PaginaAccesos = {
  filas: FilaAcceso[];
  total: number;
  pagina: number;
  porPagina: number;
  paginas: number;
  cabeza: { seq: number; hash: string } | null;
  seudonimizado: boolean;
};

/** Los filtros viajan en el CUERPO (POST), no en la URL: un nombre de profesional o un id de paciente en la query quedaría en los registros del proxy y de Caddy. */
function cuerpoDeFiltros(f: FiltrosAccesos, extra: Record<string, string | number> = {}): string {
  const limpio: Record<string, string | number> = {};
  for (const [k, v] of Object.entries({ ...f, ...extra })) if (v !== undefined && v !== '') limpio[k] = v as string | number;
  return JSON.stringify(limpio);
}

export async function consultarAccesosHce(token: string, filtros: FiltrosAccesos, pagina: number, porPagina = 50): Promise<PaginaAccesos> {
  const res = await fetch(vaultUrl('/api/hce/auditoria/accesos'), { method: 'POST', headers: bearer(token), body: cuerpoDeFiltros(filtros, { pagina, porPagina }) });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error('Sesión vencida.');
  if (!res.ok) throw new Error(body?.error || 'No se pudo cargar el registro de accesos.');
  return body as PaginaAccesos;
}

export type ResultadoExportacionAccesos = { nombre: string; sha256: string; filas: number; constancia: string | null; cadenaOk: boolean };

/** Descarga la exportación (CSV o PDF) con los mismos filtros del visor y devuelve el SHA-256 y la constancia que el servidor anotó en la cadena inmutable. */
export async function exportarAccesosHce(token: string, formato: 'csv' | 'pdf', filtros: FiltrosAccesos): Promise<ResultadoExportacionAccesos> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/accesos-${formato}`), { method: 'POST', headers: bearer(token), body: cuerpoDeFiltros(filtros) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || 'No se pudo generar la exportación.');
  }
  const info = {
    sha256: res.headers.get('x-exportacion-sha256') ?? '',
    filas: Number(res.headers.get('x-exportacion-filas') ?? 0),
    constancia: res.headers.get('x-exportacion-constancia') || null,
    cadenaOk: res.headers.get('x-exportacion-cadena') === 'ok',
  };
  const disposition = res.headers.get('content-disposition') ?? '';
  const nombre = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `accesos.${formato}`;
  await descargarArchivo(res, nombre);
  return { nombre, ...info };
}

export type VerificacionExportacion = { encontrada: boolean; seq?: number; cuando?: string; formato?: string; filas?: number; coincide?: 'archivo' | 'datos' };

export async function verificarExportacionAccesos(token: string, sha256: string): Promise<VerificacionExportacion> {
  const res = await fetch(vaultUrl(`/api/hce/auditoria/accesos-verificar?sha256=${encodeURIComponent(sha256)}`), { headers: bearer(token) });
  if (!res.ok) throw new Error('No se pudo verificar la exportación.');
  return (await res.json()) as VerificacionExportacion;
}

/** SHA-256 (hex) de un archivo, calculado en el navegador: es lo que se busca en la cadena inmutable. */
export async function sha256DeArchivo(archivo: Blob): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', await archivo.arrayBuffer());
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
